export function resolveClientExternalAgentIds(clientData) {
  const clients = clientData?.clients || clientData || {};

  return {
    referring: String(
      clients.external_referring_agent ?? clients.externalReferringAgent ?? "",
    ).trim(),
    processing: String(
      clients.external_processing_agent ?? clients.externalProcessingAgent ?? "",
    ).trim(),
  };
}

export function resolveInvoiceExternalAgentIds(invoice) {
  if (!invoice || typeof invoice !== "object") {
    return { referring: "", processing: "" };
  }

  return {
    referring: String(
      invoice.external_referring_agent ?? invoice.externalReferringAgent ?? "",
    ).trim(),
    processing: String(
      invoice.external_processing_agent ?? invoice.externalProcessingAgent ?? "",
    ).trim(),
  };
}

export function sortExternalAgentOptions(options = []) {
  return [...(Array.isArray(options) ? options : [])].sort((left, right) =>
    String(left?.name || "").localeCompare(String(right?.name || "")),
  );
}

export function resolveAgentAgencyName(agent = {}) {
  return String(
    agent?.agencyname ??
      agent?.agencyName ??
      agent?.name ??
      agent?.label ??
      "",
  ).trim();
}

export function resolveAgentEmail(agent = {}) {
  return String(agent?.email ?? agent?.agency_email ?? agent?.agencyEmail ?? "").trim();
}

export function resolveAgentPhone(agent = {}) {
  return String(
    agent?.contact ?? agent?.phone ?? agent?.mobile ?? agent?.agency_phone ?? "",
  ).trim();
}

export function resolveExternalAgentRecord(
  agentId,
  referringAgents = [],
  processingAgents = [],
) {
  const normalizedId = String(agentId ?? "").trim();
  if (!normalizedId) return null;

  const match = [...(referringAgents || []), ...(processingAgents || [])].find(
    (agent) => String(agent?.id ?? "") === normalizedId,
  );

  return match || null;
}

/** Bill-to profile for finance invoices addressed to an external agency. */
export function buildExternalAgentDisplayProfile(agent = {}) {
  const fullName = resolveAgentAgencyName(agent);
  const email = resolveAgentEmail(agent);
  const phone = resolveAgentPhone(agent);

  return {
    id: agent?.id ?? null,
    fullName,
    email,
    phone,
    city: "",
    country: "",
    description: "",
    address: "",
    isAgencyBillTo: true,
  };
}

export function buildExternalAgentLabelMap(referring = [], processing = []) {
  const map = new Map();
  [...(referring || []), ...(processing || [])].forEach((agent) => {
    const id = agent?.id;
    if (id == null || id === "") return;
    const label = resolveAgentAgencyName(agent);
    if (label) map.set(String(id), label);
  });
  return map;
}

export function normalizeAgentSearchResults(response) {
  const payload =
    response?.data?.data?.data ??
    response?.data?.data ??
    response?.data ??
    null;
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];

  return list
    .map((item) => {
      const id = item?.id ?? null;
      const label = resolveAgentAgencyName(item);
      if (!id || !label) return null;
      return {
        id,
        label,
        type: item?.type ?? item?.agent_type ?? "",
        raw: item,
      };
    })
    .filter(Boolean);
}

function normalizeAgentTypeValue(type) {
  const value = String(type ?? "").trim().toLowerCase();
  if (!value) return "";
  if (value.includes("referr")) return "referring";
  if (value.includes("process")) return "processing";
  if (value === "2") return "referring";
  if (value === "5") return "processing";
  if (value === "7") return "both";
  return value;
}

export function filterAgentSearchResults(results = [], kind = "referring") {
  const normalizedKind = kind === "processing" ? "processing" : "referring";

  return (Array.isArray(results) ? results : []).filter((item) => {
    const agentType = normalizeAgentTypeValue(item?.type);
    if (!agentType) return true;
    if (agentType === "both") return true;
    if (normalizedKind === "referring") {
      return agentType === "referring";
    }
    return agentType === "processing";
  });
}

export function buildMixedContactSearchSuggestions({
  clients = [],
  referringAgents = [],
  processingAgents = [],
  query = "",
} = {}) {
  const normalizedQuery = String(query || "").trim().toLowerCase();
  if (normalizedQuery.length < 2) return [];

  const suggestions = [];

  (Array.isArray(clients) ? clients : []).forEach((item) => {
    const id = item?.id ?? null;
    const label = String(item?.label || "").trim();
    if (!id || !label) return;
    if (!label.toLowerCase().includes(normalizedQuery)) return;
    suggestions.push({
      ...item,
      kind: "client",
      key: `client-${id}`,
    });
  });

  const agencyById = new Map();
  const addAgentMatch = (agent, role) => {
    const id = agent?.id ?? null;
    const label = resolveAgentAgencyName(agent);
    if (!id || !label || !label.toLowerCase().includes(normalizedQuery)) return;

    const key = String(id);
    const existing = agencyById.get(key);
    if (existing) {
      if (!existing.roles.includes(role)) existing.roles.push(role);
      return;
    }

    agencyById.set(key, {
      id,
      label,
      roles: [role],
      raw: agent,
    });
  };

  (Array.isArray(referringAgents) ? referringAgents : []).forEach((agent) =>
    addAgentMatch(agent, "referring"),
  );
  (Array.isArray(processingAgents) ? processingAgents : []).forEach((agent) =>
    addAgentMatch(agent, "processing"),
  );

  agencyById.forEach((entry) => {
    const kind =
      entry.roles.includes("referring") && entry.roles.includes("processing")
        ? "both"
        : entry.roles[0];
    suggestions.push({
      id: entry.id,
      label: entry.label,
      kind,
      key: `agent-${entry.id}`,
      raw: entry.raw,
    });
  });

  return suggestions;
}

/** Client-side agency search — uses getExternalAgents lists (agency name only). */
export function searchExternalAgentOptions(agents = [], query = "") {
  const normalizedQuery = String(query || "").trim().toLowerCase();
  if (!normalizedQuery || normalizedQuery.length < 2) return [];

  return (Array.isArray(agents) ? agents : [])
    .map((agent) => {
      const id = agent?.id ?? null;
      const label = resolveAgentAgencyName(agent);
      if (!id || !label) return null;
      return { id, label, raw: agent };
    })
    .filter(
      (item) =>
        item && item.label.toLowerCase().includes(normalizedQuery),
    );
}
